import React, { useLayoutEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { GoArrowUpRight } from 'react-icons/go';
import { usePageNavigation } from '../../lib/contexts/PageNavigationContext';
import './CardNav.css';

type CardNavLink = {
  label: string;
  href: string;
  ariaLabel: string;
  external?: boolean;
};

export type CardNavItem = {
  label: string;
  links: CardNavLink[];
};

export interface CardNavProps {
  logo: string;
  logoAlt?: string;
  items: CardNavItem[];
  className?: string;
  ease?: string;
  onLogoClick?: () => void;
  ctaText?: string;
  onCtaClick?: () => void;
  children?: React.ReactNode; // For additional controls like language/theme toggles
}

const CardNav: React.FC<CardNavProps> = ({
  logo,
  logoAlt = 'Logo',
  items,
  className = '',
  ease = 'power3.out',
  onLogoClick,
  ctaText = 'Book a Table',
  onCtaClick,
  children,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { isCardNavOpen, setIsCardNavOpen } = usePageNavigation();
  const navRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const tlRef = useRef<gsap.core.Timeline | null>(null);

  const calculateHeight = () => {
    const navEl = navRef.current;
    if (!navEl) return 320;

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      const contentEl = navEl.querySelector('.card-nav-content') as HTMLElement;
      if (contentEl) {
        const wasVisible = contentEl.style.visibility;
        const wasPointerEvents = contentEl.style.pointerEvents;
        const wasPosition = contentEl.style.position;
        const wasHeight = contentEl.style.height;

        contentEl.style.visibility = 'visible';
        contentEl.style.pointerEvents = 'auto';
        contentEl.style.position = 'static';
        contentEl.style.height = 'auto';

        void contentEl.offsetHeight;

        const topBar = 80;
        const padding = 16;
        const contentHeight = contentEl.scrollHeight;
        const controlsHeight = children ? 60 : 0; // Space for mobile controls

        contentEl.style.visibility = wasVisible;
        contentEl.style.pointerEvents = wasPointerEvents;
        contentEl.style.position = wasPosition;
        contentEl.style.height = wasHeight;

        return topBar + contentHeight + padding + controlsHeight;
      }
    }
    return 320;
  };

  const createTimeline = () => {
    const navEl = navRef.current;
    if (!navEl) return null;

    gsap.set(navEl, { height: 80, overflow: 'hidden' });
    gsap.set(cardsRef.current, { y: 50, opacity: 0 });

    const tl = gsap.timeline({ paused: true });

    tl.to(navEl, {
      height: calculateHeight,
      duration: 0.4,
      ease,
      onStart: () => {
        navEl.classList.remove('collapsed');
        navEl.classList.add('expanded');
      },
    });

    tl.to(cardsRef.current, { y: 0, opacity: 1, duration: 0.4, ease, stagger: 0.08 }, '-=0.1');

    // Add reverse callback to handle collapse
    tl.eventCallback('onReverseComplete', () => {
      navEl.classList.remove('expanded');
      navEl.classList.add('collapsed');
    });

    return tl;
  };

  useLayoutEffect(() => {
    const tl = createTimeline();
    tlRef.current = tl;

    // Initialize with collapsed state
    if (navRef.current) {
      navRef.current.classList.add('collapsed');
    }

    return () => {
      tl?.kill();
      tlRef.current = null;
    };
  }, [ease, items]);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (!tlRef.current) return;

      if (isExpanded) {
        const newHeight = calculateHeight();
        gsap.set(navRef.current, { height: newHeight });

        // Ensure expanded class is maintained
        if (navRef.current) {
          navRef.current.classList.remove('collapsed');
          navRef.current.classList.add('expanded');
        }

        tlRef.current.kill();
        const newTl = createTimeline();
        if (newTl) {
          newTl.progress(1);
          tlRef.current = newTl;
        }
      } else {
        // Ensure collapsed class is maintained
        if (navRef.current) {
          navRef.current.classList.remove('expanded');
          navRef.current.classList.add('collapsed');
        }

        tlRef.current.kill();
        const newTl = createTimeline();
        if (newTl) {
          tlRef.current = newTl;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded]);

  const toggleMenu = () => {
    const tl = tlRef.current;
    if (!tl) return;

    if (!isExpanded) {
      setIsCardNavOpen(true);
      setIsExpanded(true);
      tl.play(0);
    } else {
      setIsCardNavOpen(false);
      tl.eventCallback('onReverseComplete', () => {
        setIsExpanded(false);
        // Ensure collapsed class is applied
        if (navRef.current) {
          navRef.current.classList.remove('expanded');
          navRef.current.classList.add('collapsed');
        }
      });
      tl.reverse();
    }
  };

  const setCardRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) cardsRef.current[i] = el;
  };

  const handleLogoClick = () => {
    if (onLogoClick) {
      onLogoClick();
    } else {
      window.location.href = '/';
    }
  };

  const handleCtaClick = () => {
    if (onCtaClick) {
      onCtaClick();
    } else {
      window.location.href = '/reservations';
    }
  };

  return (
    <div className={`card-nav-container ${className}`}>
      <nav ref={navRef} className={`card-nav ${isExpanded ? 'open expanded' : 'collapsed'}`}>
        <div className="card-nav-top">
          <div
            className={`hamburger-menu ${isCardNavOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            role="button"
            aria-label={isExpanded ? 'Close menu' : 'Open menu'}
            tabIndex={0}
          >
            <div className="hamburger-line" />
            <div className="hamburger-line" />
          </div>

          <div className="logo-container">
            <button
              onClick={handleLogoClick}
              className="flex items-center gap-2 bg-transparent border-none cursor-pointer"
              aria-label="Go to homepage"
            >
              <img src={logo} alt={logoAlt} className="logo" />
            </button>
          </div>

          {/* Desktop Controls */}
          <div className="card-nav-controls hidden lg:flex">{children}</div>

          <button type="button" className="card-nav-cta-button" onClick={handleCtaClick}>
            {ctaText}
          </button>
        </div>

        <div className="card-nav-content" aria-hidden={!isExpanded}>
          {(items || []).slice(0, 3).map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className={`nav-card nav-card-${idx + 1}`}
              ref={setCardRef(idx)}
            >
              <div className="nav-card-label">{item.label}</div>
              <div className="nav-card-links">
                {item.links?.map((lnk, i) => (
                  <a
                    key={`${lnk.label}-${i}`}
                    className="nav-card-link"
                    href={lnk.href}
                    aria-label={lnk.ariaLabel}
                    {...(lnk.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    <GoArrowUpRight className="nav-card-link-icon" aria-hidden="true" />
                    {lnk.label}
                  </a>
                ))}
              </div>
            </div>
          ))}

          {/* Mobile Controls */}
          {children && <div className="mobile-controls lg:hidden">{children}</div>}
        </div>
      </nav>
    </div>
  );
};

export default CardNav;
